import * as api from '../api';
import {EVENT_PRECISION, EVENT_TYPE, COLOR, AUTORITES, HORAIRES, INFORMATIONS, EVENT_ORIGIN, EVENT_SUITE} from "./enum";
import {uploadFile, writeClipboardText, isDeepEqual, getActivityName} from './utils';
import {STORAGE_KEYS, Storage} from './store';
import {smsD3} from "./sms";
import {sendTchapMessage} from "./tchap";

function buildEventTemplateContext(event, squad) {
    const helicosPartners = Storage.get(STORAGE_KEYS.HELICO) ?? [];
    const helicos = (squad?.helicopter_patners?.filter(helico => helico.available) || []).concat(helicosPartners);
    const uniqueHelicos = Array.from(new Map(helicos.map(h => [h.id, h])).values());
    const lastStatusKey = (event.last_status ?? '').split(' ')[0];
    const color = event.open ? (COLOR[lastStatusKey] ?? 'red') : 'grey';
    const displayId = event.local_id;
    const helicopter = uniqueHelicos.find(h => h.id === (event.helicopter?.id || event.helicopter));
    const hasCoordinates = event.features?.some(f => f.properties?.type === 'event' || f.properties?.type?.name === 'event');
    const user = Storage.get(STORAGE_KEYS.USER_DETAILS);
    const canDelete = (user?.username === event.created_by) ||
                      (user?.unit?.code === event.squad_code && (user?.groups || []).includes('niveau2'));

    return {
        event,
        helicopter,
        displayId,
        hasCoordinates,
        canDelete,
        activityName: getActivityName(event.activity),
        accidentTypeName: Storage.get(STORAGE_KEYS.ACCIDENT_TYPE)
            ?.find(a => a.id === (event.accident_type?.id || event.accident_type))?.name,
        pathologyNames: Storage.get(STORAGE_KEYS.PATHOLOGY)
            ?.flatMap(cat => cat.pathologies)
            ?.filter(p => (event.pathologies || []).some(ep => ep === p.id || ep?.id === p.id))
            ?.map(p => p.name)
            ?.join(', '),
        statusColor: color
    };
}

async function getFreshEventTemplateContext(eventId) {
    const freshEvent = await api.getEvent(eventId);
    return buildEventTemplateContext(freshEvent, window.currentEventDetailSquad || null);
}

function renderTemplateByName(templateName, context) {
    const template = Handlebars.templates[templateName];

    if (!template) {
        throw new Error(`Template Handlebars introuvable: ${templateName}`);
    }

    return template(context);
}

async function withFreshEventContext(eventId, callback) {
    const context = await getFreshEventTemplateContext(eventId);
    return callback(context);
}

function bindClickOnce(selector, datasetFlag, handler) {
    document.querySelectorAll(selector).forEach(element => {
        if (element.dataset[datasetFlag] === 'true') {
            return;
        }

        element.dataset[datasetFlag] = 'true';
        element.addEventListener('click', handler);
    });
}

function setLoadingState(element, html) {
    if (element) {
        element.innerHTML = html;
    }
}

function initDynamicEventModals() {
    const modalConfigs = [
        {
            triggerSelector: 'a[href="#tchap-modal"]',
            modalId: 'tchap-modal',
            templateName: 'alerte-modal'
        },
        {
            triggerSelector: 'a[href="#jco-modal"]',
            modalId: 'jco-modal',
            templateName: 'jco-modal'
        }
    ];

    modalConfigs.forEach(({triggerSelector, modalId, templateName}) => {
        bindClickOnce(triggerSelector, 'dynamicModalListenerAttached', async () => {
            const modal = document.getElementById(modalId);
            const content = modal?.querySelector('.modal-content');
            const actionButton = modal?.querySelector('[data-event-id]');
            const eventId = actionButton?.dataset.eventId;

            if (!content || !eventId) {
                return;
            }

            setLoadingState(content, '<p>Chargement…</p>');

            try {
                await withFreshEventContext(eventId, (context) => {
                    content.innerHTML = renderTemplateByName(templateName, context);
                });
            } catch (error) {
                console.error(`Erreur lors du rendu de la modale ${modalId}:`, error);
                setLoadingState(content, '<p>Erreur lors du chargement.</p>');
            }
        });
    });
}

export function initClipboardListener() {
    bindClickOnce('[data-action="clipboard-copy"]', 'clipboardListenerAttached', async (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();

        const button = clickEvent.currentTarget;
        const eventId = button.dataset.eventId;
        const templateName = button.dataset.clipboardTemplate;

        if (!eventId || !templateName) {
            console.error('Paramètres manquants pour la copie');
            M.toast({html: 'Paramètres de copie invalides', classes: 'red'});
            return;
        }

        button.disabled = true;

        try {
            await withFreshEventContext(eventId, async (context) => {
                const text = renderTemplateByName(templateName, context);
                await writeClipboardText(text);
            });

            M.toast({html: 'Texte copié', classes: 'green'});

            const modal = button.closest('.modal');
            if (modal) {
                M.Modal.getInstance(modal)?.close();
            }
        } catch (error) {
            console.error('Erreur lors de la copie dans le presse-papiers:', error);
            M.toast({html: 'Erreur lors de la copie', classes: 'red'});
        } finally {
            button.disabled = false;
        }
    });
}

export function initSmsD3Listener() {
    bindClickOnce('[data-action="sms-d3"]', 'smsListenerAttached', async (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();

        const button = clickEvent.currentTarget;
        const eventId = button.dataset.smsEvent;
        const phone = button.dataset.smsPhone;
        const text = button.dataset.smsText;
        const helico = button.dataset.smsHelico;

        if (!eventId || !phone || !text) {
            console.error('Paramètres manquants pour smsD3');
            M.toast({html: 'Paramètres SMS D3 invalides', classes: 'red'});
            return;
        }

        if (!confirm(`Confirmation de l'envoi d'un SMS à l'hélicoptère ${helico} ?`)) {
            return;
        }

        button.disabled = true;

        try {
            await smsD3(eventId, phone, text);
            M.toast({html: 'SMS D3 envoyé', classes: 'green'});

            const modal = button.closest('.modal');
            if (modal) {
                M.Modal.getInstance(modal)?.close();
            }
        } catch (error) {
            console.error('Erreur lors de l’envoi du SMS D3:', error);
            M.toast({html: 'Erreur lors de l’envoi du SMS D3', classes: 'red'});
        } finally {
            button.disabled = false;
        }
    });
}

export function initTchapListener() {
    bindClickOnce('[data-action="tchap-message"]', 'tchapListenerAttached', async (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();

        const button = clickEvent.currentTarget;
        const eventId = button.dataset.eventId;
        const textTemplateName = button.dataset.tchapTextTemplate;
        const htmlTemplateName = button.dataset.tchapHtmlTemplate;

        if (!eventId || !textTemplateName) {
            console.error('Paramètres manquants pour sendTchapMessage');
            M.toast({html: 'Paramètres Tchap invalides', classes: 'red'});
            return;
        }

        if (!confirm(`Confirmation de l'envoi du message sur Tchap ?`)) {
            return;
        }

        button.disabled = true;

        try {
            await withFreshEventContext(eventId, async (context) => {
                const text = renderTemplateByName(textTemplateName, context);
                const texteHtml = htmlTemplateName ? renderTemplateByName(htmlTemplateName, context) : '';

                await sendTchapMessage(eventId, text, texteHtml);
            });

            M.toast({html: 'Message Tchap envoyé', classes: 'green'});

            const modal = button.closest('.modal');
            if (modal) {
                M.Modal.getInstance(modal)?.close();
            }
        } catch (error) {
            console.error('Erreur lors de l’envoi du message Tchap:', error);
            M.toast({html: 'Erreur lors de l’envoi du message Tchap', classes: 'red'});
        } finally {
            button.disabled = false;
        }
    });
}

function updateEventFactory(event, map) {
    return (value, name) => {
        if (event[name] === value) {
            return;
        }

        if (Array.isArray(value) && Array.isArray(event[name]) && isDeepEqual(value, event[name])) {
            return;
        }

        event[name] = value;

        if (map) {
            map._isSavingEvent = true;
        }

        api.saveEvent(event)
            .then((updatedEvent) => {
                Object.assign(event, updatedEvent);

                if (map && map.event) {
                    map.event = updatedEvent;

                    // const shouldRenderDetails = ['message_attachements', 'helicopter', 'support_units', 'open', 'is_med'].includes(name);
                    const shouldRenderDetails = ['message_attachements', 'support_units', 'open', 'helicopter', 'attachements'].includes(name);
                    const shouldUpdateDetailsHeader = ['local_id', 'activity'].includes(name);

                    if (map._eventLayer && typeof map._eventLayer.update === 'function') {
                        map._eventLayer.update(updatedEvent, {
                            renderDetails: shouldRenderDetails
                        });
                    }

                    if (
                        shouldRenderDetails &&
                        (!map._eventLayer || typeof map._eventLayer.update !== 'function') &&
                        typeof map._renderEventDetails === 'function'
                    ) {
                        map._renderEventDetails();
                    }

                    if (shouldUpdateDetailsHeader) {
                        map._updateEventDetailsHeader?.();
                        map._updateEventLayerControlTitle?.();
                    }

                    if (updatedEvent.local_id) {
                        const displayCom = event.com
                            ?.replace(/^\s*\d{5}\s*-\s*/, '')
                            .toUpperCase()
                            .slice(0, 12);

                        document.title = `${updatedEvent.local_id} - ${displayCom ?? ''}`;
                    }
                }

                setTimeout(() => {
                    if (map) {
                        map._isSavingEvent = false;
                        map._flushPendingDetailedEventUpdate?.();
                    }
                }, 700);

                M.toast({html: 'Modification sauvegardée', classes: 'green accent-4'});
            })
            .catch((error) => {
                if (map) {
                    map._isSavingEvent = false;
                    map._flushPendingDetailedEventUpdate?.();
                }
                throw error;
            });
    };
}


//Gestion des selectBoxes select2
function select2Template_category(data) {
    if (!data.id) {
        return data.text;
    }
    if (data.children) {
        return $('<span style="font-weight: bold; color: #1565c0; text-transform: uppercase; font-size: 0.85rem;">' + data.text + '</span>');
    }
    return $('<span>' + data.text + '</span>');
}

function select2Data_activity() {
    const activities = Storage.get(STORAGE_KEYS.ACTIVITY);
    return (activities || []).map(category => ({
        id: category.id,
        text: category.name,
        children: (category.activities || []).map(activity => ({
            id: activity.id,
            text: activity.name
        }))
    }));
}

function select2Data_pathologies(accidentId) {
    const accident_types = Storage.get(STORAGE_KEYS.ACCIDENT_TYPE);
    if (!accidentId) return [];
    const accident = (accident_types || []).find(type => type.id === accidentId);
    if (!accident) return [];
    return (accident.pathology_categories || []).map(category => ({
        id: category.id,
        text: category.name,
        children: (category.pathologies || []).map(pathology => ({
            id: pathology.id,
            text: pathology.name
        }))
    }));
}

function select2Data_accident(activityId) {
    const accident_types = Storage.get(STORAGE_KEYS.ACCIDENT_TYPE);
    if (!activityId) return [];
    return (accident_types || [])
        .filter(type => (type.activity_categories || []).some(cat => (cat.activities || []).some(act => act.id === activityId)))
        .map(type => ({
            id: type.id,
            text: type.name
        }));
}

export function initSelect2(event) {
    $('#js-activity-single').empty().append('<option></option>').select2({
        data: select2Data_activity(),
        placeholder: "Sélectionne une activité",
        allowClear: true,
        width: '100%',
        theme: 'classic',
        templateResult: select2Template_category
    }).on('change', function (e) {
        const activityId = parseInt($(this).val(), 10);
        const accidentSelect = $('#js-accident-single');
        const pathologiesSelect = $('#js-pathologies-multiple');

        // Ré-initialiser l'accident avec les données filtrées si possible
        const currentAccidentId = accidentSelect.val();
        const accidentData = select2Data_accident(activityId);
        accidentSelect.empty().append('<option></option>');
        accidentSelect.select2({
            data: accidentData,
            placeholder: "Sélectionne un type d'accident",
            allowClear: true,
            width: '100%',
            theme: 'classic'
        });

        // Restaurer la sélection de l'accident si elle est toujours valide
        if (currentAccidentId && accidentData.some(d => d.id == currentAccidentId)) {
            accidentSelect.val(currentAccidentId).trigger('change.select2');
        }

        // Mettre à jour les pathologies possibles sans vider la sélection actuelle
        const currentPathologies = pathologiesSelect.val() || [];
        const pathologiesData = select2Data_pathologies(currentAccidentId);
        pathologiesSelect.empty();
        pathologiesSelect.select2({
            data: pathologiesData,
            placeholder: "Sélectionne des pathologies",
            allowClear: true,
            width: '100%',
            theme: 'classic',
            templateResult: select2Template_category
        });

        // Restaurer les pathologies qui sont toujours valides
        const validPathologies = currentPathologies.filter(pId => {
            return pathologiesData.some(cat => (cat.children || []).some(p => p.id == pId));
        });
        if (validPathologies.length > 0) {
            pathologiesSelect.val(validPathologies).trigger('change.select2');
        }

        if (e.originalEvent || (e.target && $(e.target).data('select2'))) {
            window.updateEvent(activityId, 'activity');
        }
    });

    if (event?.activity) {
        $('#js-activity-single').val(event.activity?.id || event.activity).trigger('change.select2');
    }

    $('#js-accident-single').empty().append('<option></option>').select2({
        data: event?.activity ? select2Data_accident(event.activity?.id || event.activity) : [],
        placeholder: event?.activity ? "Sélectionne un type d'accident" : "Sélectionne une activité d'abord",
        allowClear: true,
        width: '100%',
        theme: 'classic'
    }).on('change', function (e) {
        const accidentId = parseInt($(this).val(), 10);
        const pathologiesSelect = $('#js-pathologies-multiple');

        // Mettre à jour les pathologies sans vider la sélection actuelle
        const currentPathologies = pathologiesSelect.val() || [];
        const pathologiesData = select2Data_pathologies(accidentId);
        pathologiesSelect.empty();
        pathologiesSelect.select2({
            data: pathologiesData,
            placeholder: "Sélectionne des pathologies",
            allowClear: true,
            width: '100%',
            theme: 'classic',
            templateResult: select2Template_category
        });

        // Restaurer les pathologies qui sont toujours valides
        const validPathologies = currentPathologies.filter(pId => {
            return pathologiesData.some(cat => (cat.children || []).some(p => p.id == pId));
        });
        if (validPathologies.length > 0) {
            pathologiesSelect.val(validPathologies).trigger('change.select2');
        }

        if (e.originalEvent || (e.target && $(e.target).data('select2'))) {
            window.updateEvent(accidentId, 'accident_type');
        }
    });

    if (event?.accident_type) {
        $('#js-accident-single').val(event.accident_type?.id || event.accident_type).trigger('change.select2');
    }

    $('#js-pathologies-multiple').empty().select2({
        data: event?.accident_type ? select2Data_pathologies(event.accident_type?.id || event.accident_type) : [],
        placeholder: event?.accident_type ? "Sélectionne des pathologies" : "Sélectionne un type d'accident d'abord",
        allowClear: true,
        width: '100%',
        theme: 'classic',
        templateResult: select2Template_category
    }).on('change', function (e) {
        if (e.originalEvent || (e.target && $(e.target).data('select2'))) {
            const values = $(this).val().map(v => parseInt(v, 10));
            window.updateEvent(values, 'pathologies');
        }
    });

    if (event?.pathologies) {
        const pathIds = event.pathologies.map(p => p.id || p);
        $('#js-pathologies-multiple').val(pathIds).trigger('change.select2');
    }
}

function shareWith() {
    const toShareWith = Array.from(document.getElementsByName('share_with'))
        .filter(elem => elem.checked)
        .map(elem => ({squad: elem.value}));

    window.updateEvent(toShareWith, 'support_units');
    M.Modal.getInstance(document.getElementById('share-modal')).close();
}

function addMessageFactory(event) {
    return (e, type, value) => {
        if (!('stopPropagation' in e)) {
            return;
        }

        e.stopPropagation();
        e.preventDefault();

        if (!event.message_attachements) {
            event.message_attachements = [];
        }

        const msgs = [...(event.message_attachements || []), {type, content: value}];
        window.updateEvent(msgs, 'message_attachements');

        const statusModal = document.getElementById('status-modal');
        const noteModal = document.getElementById('note-modal');

        if (type === 'status' && statusModal) {
            M.Modal.getInstance(statusModal)?.close();
        } else if (type === 'message' && noteModal) {
            M.Modal.getInstance(noteModal)?.close();
            const textarea = document.getElementById('message');
            if (textarea) {
                textarea.value = '';
                textarea.style.height = 'auto';
            }
        }
    };
}

export function updateSmsStatus(data) {
    const div = document.getElementById(`sms_${data.id}`);
    if (div) {
            const statusIcons = {
                'Envoi en cours': '<i class="fa-solid fa-check" style="color: grey;"></i>',
                SENT: '<i class="fa-solid fa-check" style="color: grey;"></i>',
                RECEIVED: '<i class="fa-solid fa-check-double" style="color: green;"></i>',
                PERMISSION: '<i class="fa-solid fa-xmark" style="color: red;"></i>',
                GPS_TIMEOUT: '<i class="fa-solid fa-xmark" style="color: red;"></i>',
                OK: '<i class="fa-solid fa-location-dot" style="color: green;"></i>'
            };

            const icon = statusIcons[data.status] ?? data.status;
            const updatedAt = data.updated_at ?? data.updates_at ?? '';

            div.innerHTML = `<span title="${updatedAt} ${data.user_agent ? ' ' + data.user_agent : ''}">${icon}</span>`;    }
}

async function initSmsStatus(event, db) {
    if (!db || !event.message_attachements) {
        return;
    }

    for (const msg of event.message_attachements) {
        if (msg.reference) {
            try {
                const sms = await db.sms.get(msg.reference);
                if (sms) {
                    updateSmsStatus(sms);
                }
            } catch (error) {
                console.error(`Erreur lors de la récupération du SMS ${msg.reference}:`, error);
            }
        }
    }
}

function closeEventFactory(event) {
    return () => {
        api.closeEvent(event.id)
            .then((file) => {
                const url = window.URL.createObjectURL(file);
                const a = document.createElement("a");
                
                a.href = url;

                a.download = `${event.local_id}${event.name ? '-' + event.name : ''}-${new Date(event.created_at).toISOString()}.zip`;
                document.body.appendChild(a);
                a.click();

                // Cleanup
                a.remove();
                window.URL.revokeObjectURL(url);
            });
    };
}

export async function displayEventDetails(event, map, squad, db) {

    window.currentEventDetailSquad = squad;
    window.upload = uploadFile(map, '.jpg,.png,.doc,.pdf,.txt,.odt');

    if (!('shareWith' in window)) {
        window.shareWith = shareWith;
    }

    window.updateEvent = updateEventFactory(event, map);
    window.closeEventAndExport = closeEventFactory(event);

    window.addMessage = addMessageFactory(event);
    window.deleteAttachment = (eventId, attachmentId) => {
        if (confirm('Supprimer cette pièce jointe ?')) {
            api.deleteEventAttachment(eventId, attachmentId).then(() => {
                M.toast({html: 'Pièce jointe supprimée', classes: 'green accent-4'});
                const btn = document.querySelector(`button[onclick="window.deleteAttachment(${eventId}, ${attachmentId})"]`);
                if (btn) btn.closest('tr').remove();
            });
        }
    };
    window.deleteEventConfirm = (eventId) => {
        api.deleteEvent(eventId).then(() => {
            M.toast({html: 'Événement supprimé', classes: 'green accent-4'});
            setTimeout(() => window.close(), 1000);
        });
    };

    const displayActivities = Storage.get(STORAGE_KEYS.ACTIVITY)
        .sort((a, b) => a.order - b.order);

    let displayAccidentTypes = Storage.get(STORAGE_KEYS.ACCIDENT_TYPE);

    if (event.activity) {
        displayAccidentTypes = displayAccidentTypes
            .filter(({activity_categories}) => activity_categories
                .some(({activities}) => activities
                    .some(({id}) => id === (event.activity?.id || event.activity))));
    }

    let displayPathologies = [];

    if (event.accident_type) {
        const accidentType = Storage.get(STORAGE_KEYS.ACCIDENT_TYPE)
            .find(type => type.id === (event.accident_type?.id || event.accident_type));

        displayPathologies = accidentType?.pathology_categories ?? [];
    }

    const helicosPartners = Storage.get(STORAGE_KEYS.HELICO) ?? [];
    const helicos = (squad.helicopter_patners?.filter(helico => helico.available) || []).concat(helicosPartners);
    const uniqueHelicos = Array.from(new Map(helicos.map(h => [h.id, h])).values());
    const partners = squad.partners || [];
    const color = event.open ? (COLOR[event.last_status] ?? 'red') : 'grey';
    const helicopter = uniqueHelicos.find(h => h.id === (event.helicopter?.id || event.helicopter));
    const displayId = event.local_id;
    const hasCoordinates = event.features?.some(f => f.properties?.type === 'event' || f.properties?.type?.name === 'event');
    const user = Storage.get(STORAGE_KEYS.USER_DETAILS);
    const canDelete = (user?.username === event.created_by) ||
                      (user?.unit?.code === event.squad_code && (user?.groups || []).includes('niveau2'));

    const result = Handlebars.templates['event-details']({
        event,
        helicopter,
        displayId,
        hasCoordinates,
        canDelete,
        activityName: getActivityName(event.activity),
        accidentTypeName: Storage.get(STORAGE_KEYS.ACCIDENT_TYPE)
            ?.find(a => a.id === (event.accident_type?.id || event.accident_type))?.name,
        pathologyNames: Storage.get(STORAGE_KEYS.PATHOLOGY)
            ?.flatMap(cat => cat.pathologies)
            ?.filter(p => (event.pathologies || []).some(ep => ep === p.id || ep?.id === p.id))
            ?.map(p => p.name)
            ?.join(', '),
        activities: displayActivities,
        pathologies: displayPathologies,
        accidentTypes: displayAccidentTypes,
        helicos: uniqueHelicos,
        patners: partners,
        statusColor: color,
        status: COLOR,
        autorites: AUTORITES,
        informations: INFORMATIONS,
        horaires: HORAIRES,
        eventTypes: EVENT_TYPE,
        eventOrigins: EVENT_ORIGIN,
        eventSuites: EVENT_SUITE,
        eventPrecision: EVENT_PRECISION
    });

    setTimeout(() => {
        initDynamicEventModals();
        initClipboardListener();
        initSmsD3Listener();
        initTchapListener();

        if (event.open) {
            initSelect2(event);
        }

        if (db) {
            initSmsStatus(event, db);
        }
    }, 0);

    return result;
}